// MachineCategoryStep - Machine category selection for worksheet wizard
// 6 categories: Electric Small/Large, Petrol Handheld/Push, Tractor, Battery
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { BatteryCharging, Cable, Flame, Tractor, Truck, Zap, type LucideIcon } from 'lucide-react';
import { useWorksheetWizardStore } from '../wizard-store';

interface MachineCategoryOption {
  code: string;
  labelKey: string;
  color: {
    ring: string;
    bg: string;
    icon: string;
  };
  Icon: LucideIcon;
}

const MACHINE_CATEGORY_OPTIONS: MachineCategoryOption[] = [
  {
    code: 'ELECTRIC_SMALL',
    labelKey: 'worksheet.machineCategory.electricSmall',
    color: {
      ring: 'ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      icon: 'text-yellow-600 dark:text-yellow-400',
    },
    Icon: Zap,
  },
  {
    code: 'ELECTRIC_LARGE',
    labelKey: 'worksheet.machineCategory.electricLarge',
    color: {
      ring: 'ring-orange-500 bg-orange-50 dark:bg-orange-900/20',
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      icon: 'text-orange-600 dark:text-orange-400',
    },
    Icon: Cable,
  },
  {
    code: 'PETROL_HANDHELD',
    labelKey: 'worksheet.machineCategory.petrolHandheld',
    color: {
      ring: 'ring-red-500 bg-red-50 dark:bg-red-900/20',
      bg: 'bg-red-100 dark:bg-red-900/30',
      icon: 'text-red-600 dark:text-red-400',
    },
    Icon: Flame,
  },
  {
    code: 'PETROL_PUSH',
    labelKey: 'worksheet.machineCategory.petrolPush',
    color: {
      ring: 'ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      icon: 'text-emerald-600 dark:text-emerald-400',
    },
    Icon: Truck,
  },
  {
    code: 'TRACTOR',
    labelKey: 'worksheet.machineCategory.tractor',
    color: {
      ring: 'ring-blue-500 bg-blue-50 dark:bg-blue-900/20',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      icon: 'text-blue-600 dark:text-blue-400',
    },
    Icon: Tractor,
  },
  {
    code: 'BATTERY',
    labelKey: 'worksheet.machineCategory.battery',
    color: {
      ring: 'ring-green-500 bg-green-50 dark:bg-green-900/20',
      bg: 'bg-green-100 dark:bg-green-900/30',
      icon: 'text-green-600 dark:text-green-400',
    },
    Icon: BatteryCharging,
  },
];

export function MachineCategoryStep() {
  const { t } = useTranslation();
  const { machineCategoryId, setMachineCategory } = useWorksheetWizardStore();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('worksheet.machineCategory.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('worksheet.machineCategory.subtitle')}
        </p>
      </div>

      {/* Machine category cards - 2x3 grid */}
      <div className="grid gap-4 sm:grid-cols-3">
        {MACHINE_CATEGORY_OPTIONS.map(option => {
          const isSelected = machineCategoryId === option.code;

          return (
            <Card
              key={option.code}
              className={cn(
                'relative cursor-pointer p-5 transition-all hover:shadow-md border-2',
                isSelected
                  ? `ring-2 ${option.color.ring} shadow-lg`
                  : 'border-transparent hover:border-gray-300 dark:hover:border-[#3a9aa8]'
              )}
              onClick={() => setMachineCategory(option.code)}
            >
              <div className="flex flex-col items-center gap-3 text-center">
                {/* Icon */}
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-full',
                    option.color.bg
                  )}
                >
                  <option.Icon className={cn('h-6 w-6', option.color.icon)} />
                </div>

                {/* Label */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t(option.labelKey)}
                  </h3>
                </div>

                {/* Selection indicator */}
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-gray-300 dark:border-[#2d7d8a]'
                  )}
                >
                  {isSelected && (
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Selected category summary */}
      {machineCategoryId && (
        <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-green-800 dark:text-green-300">
              {t(
                MACHINE_CATEGORY_OPTIONS.find(o => o.code === machineCategoryId)?.labelKey ??
                  machineCategoryId
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
